import { useCallback, useState } from 'react';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconSend } from '@components/icons/send';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { VerificationCodeInput } from '@components/verification-code-input';
import { useScopedTranslate } from '@locale/translate';

import {
  useCreateEmailAddress,
  useInvalidateEmailDomains,
  useSendEmailAddressVerification,
  useVerifyEmailAddress,
} from 'api/company/hooks';
import { EmailAddress, EmailDomain } from 'api/company/requests';
import { useWebSocket } from 'api/hooks';

type ModalProps = {
  address?: EmailAddress;
  domains?: EmailDomain[];
};

export const useEmailVerificationModal = () => useModal(EmailVerificationModal);

type FormValues = {
  domainShortId: string;
  email: string;
  senderName: string;
};

const Form = createForm<FormValues>();

const EmailVerificationModal = ({ address, domains = [] }: ModalProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const [step, setStep] = useState<'form' | 'verify'>(address ? 'verify' : 'form');
  const [createdAddress, setCreatedAddress] = useState<EmailAddress | null>(address ?? null);
  const [errors, setErrors] = useState<Partial<Record<'email' | 'code', string>>>({});
  const [isVerifying, setIsVerifying] = useState(false);

  const createAddressMutation = useCreateEmailAddress();
  const sendCodeMutation = useSendEmailAddressVerification();
  const verifyMutation = useVerifyEmailAddress();
  const { invalidate } = useInvalidateEmailDomains();

  const verifiedDomains = domains.filter((d) => d.isVerified);
  const hasMultipleDomains = verifiedDomains.length > 1;
  const defaultDomain = verifiedDomains[0];

  const validateEmailDomain = (email: string, domainShortId: string): boolean => {
    const selectedDomain = verifiedDomains.find((d) => d.shortId === domainShortId);
    if (!selectedDomain || !selectedDomain.isManaged) return true;

    const emailDomain = email.split('@')[1];
    return emailDomain === selectedDomain.domain;
  };

  const handleCreateAndSend = async (values: FormValues) => {
    setErrors({});
    const domainShortId = values.domainShortId || defaultDomain?.shortId;

    if (!domainShortId) {
      setErrors({ email: t('selectDomain') });
      return;
    }

    const selectedDomain = verifiedDomains.find((d) => d.shortId === domainShortId);
    if (selectedDomain?.isManaged && !validateEmailDomain(values.email, domainShortId)) {
      setErrors({ email: t('domainMismatch', selectedDomain.domain) });
      return;
    }

    try {
      const newAddress = await createAddressMutation.mutateAsync({
        domainShortId,
        fromAddress: values.email,
        fromName: values.senderName,
      });
      setCreatedAddress(newAddress);
      setStep('verify');
    } catch (error: any) {
      const apiError = error?.response?.data?.fromAddress;
      setErrors({ email: apiError || t('createFailed') });
    }
  };

  const handleVerify = useCallback(
    async (code: string) => {
      if (!createdAddress) return;

      setErrors({});
      setIsVerifying(true);
      try {
        const result = await verifyMutation.mutateAsync({
          shortId: createdAddress.shortId,
          code,
        });
        if (result.isVerified) {
          await invalidate();
          hideModal();
        } else {
          setIsVerifying(false);
          setErrors({ code: t('invalidCode') });
        }
      } catch {
        setIsVerifying(false);
        setErrors({ code: t('verificationFailed') });
      }
    },
    [createdAddress, t, verifyMutation, invalidate]
  );

  // Listen for WebSocket notification when email is verified in another tab/device
  useWebSocket(
    `email-verification/${createdAddress?.shortId}/`,
    async () => {
      await invalidate();
      hideModal();
    },
    { enabled: step === 'verify' && !!createdAddress, throttleTime: 0 }
  );

  const isLoading =
    createAddressMutation.isLoading || sendCodeMutation.isLoading || verifyMutation.isLoading;

  if (step === 'verify' && createdAddress) {
    return (
      <Modal isLoading={isVerifying}>
        <Modal.SimpleHeader title={t('verifyEmail')} />
        <Modal.Content>
          <Stack space={6}>
            <Card variant="green" space={3} hasBorder={false}>
              <Stack space={2} orientation="horizontal" align="center">
                <IconFormIndicatorSuccess color="green100" />
                <Text text={t('emailOnWay')} font="semibold14" color="green100" />
              </Stack>
            </Card>

            <Text
              text={t('verifyDescription', createdAddress.fromAddress)}
              font="regular14"
              color="textSecondary"
            />

            <Stack space={1}>
              <Text text={t('verificationCode')} font="semibold14" />
              <VerificationCodeInput
                onComplete={handleVerify}
                error={errors.code}
                isDisabled={isVerifying}
              />
            </Stack>
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Stack orientation="horizontal" justify="flex-end">
            <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
          </Stack>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal isLoading={createAddressMutation.isLoading}>
      <Form onSubmit={handleCreateAndSend} store={formStore} overrideErrors={errors}>
        <Modal.SimpleHeader title={t('addVerifyEmail')} />
        <Modal.Content>
          <Stack space={6}>
            <Text color="textSecondary" text={t('description')} font="regular14" />

            {hasMultipleDomains && (
              <Form.Select
                id="domainShortId"
                label={t('emailDomain')}
                placeholder={t('selectDomain')}
                validators={['required']}
                options={verifiedDomains.map((domain) => ({
                  value: domain.shortId,
                  label: domain.smtpHost ?? domain.domain,
                }))}
              />
            )}

            <Form.TextField
              id="email"
              label={t('emailAddress')}
              placeholder={t('emailPlaceholder')}
              validators={['required', 'email']}
            />

            <Form.TextField
              id="senderName"
              label={t('senderName')}
              placeholder={t('senderNamePlaceholder')}
              validators={['required']}
              maxChars={100}
              helpText={t('maxLength')}
            />
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideModal} />
            <Form.Submit
              text={t('sendVerificationEmail')}
              variant="primary-blue"
              isDisabled={isLoading}
              iconLeft={<IconSend />}
            />
          </Form.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const locale = {
  en: {
    addVerifyEmail: 'Add & verify email',
    verifyEmail: 'Verify email',
    description:
      "We need to verify that you have access to the email address at the domain you plan to send from. Enter an address and we'll send you a verification email",
    emailDomain: 'Email domain',
    selectDomain: 'Select domain...',
    emailAddress: 'Email address',
    emailPlaceholder: 'hr@acme.com',
    senderName: 'Sender name',
    senderNamePlaceholder: 'Acme inc',
    sendVerificationEmail: 'Send verification email',
    emailOnWay: "Your verification email is on it's way!",
    verifyDescription:
      'We sent an email to {0} to verify the domain, click verify email in the email. Or enter the verification code in the field below',
    verificationCode: 'Verification code',
    domainMismatch: 'You can only add e-mail addresses with the verified domain: {0}',
    createFailed: 'Failed to create email address',
    invalidCode: 'The code you entered is invalid. Please try again.',
    verificationFailed: 'Verification failed. Are you sure the code is correct?',
    maxLength: 'Maximum length is 100 characters',
  },
};
